/**
 * Frame grid API for working with video durations
 * adding EPSILON to account for edge cases like: 111*SPF = 3.6999999999999997
 *
 * Frame edge times (sec):
 * {x.x00, x.x33, x.066, x.x00 ... }
 *
 * Single frame duration distribution (sec):
 * {0.033, 0.033, 0.034, 0.033 ... }
 */
const FrameGrid = {
  /**
   * seconds per frame
   * @type {number}
   */
  SPF: 1 / 30,

  /**
   * truncation precision (3 digits to the left of floating point)
   * @type {number}
   */
  PRECISION: 1e3,

  /**
   * epsilon value to fix precision errors
   * @type {number}
   */
  EPSILON: 1e-4,

  /**
   * array of frame edges
   * @type {Array<number>}
   */
  FRAME_EDGES: [0, 33, 66],

  /**
   * index of the frame left edge containing larger frame duration (34ms)
   * @type {number}
   */
  FRAME_EDGES_EXCEPTION_INDEX: 2,

  /**
   * auxiliary method to truncate times to 1e-3 precision
   * note: only for positive 32 bit numbers
   * @param time {number}
   * @returns {number}
   */
  trunc(time) {
    if (time < 0) {
      // console.warn('FrameGrid.trunc on negative number');
    }
    return ~~(time * this.PRECISION + this.EPSILON) / this.PRECISION;
  },

  /**
   * auxiliary method to truncate times to 1e-3 precision
   * note: negative numbers floored towards -Infinity
   * @param time {number}
   * @returns {number}
   */
  floor(time) {
    return Math.floor(time * this.PRECISION + this.EPSILON) / this.PRECISION;
  },

  /**
   * auxiliary method to ceil times to 1e-3 precision
   * @param time {number}
   * @returns {number}
   */
  ceil(time) {
    // return this.trunc(time);
    return Math.ceil(time * this.PRECISION + this.EPSILON) / this.PRECISION;
  },

  /**
   * auxiliary method to round times to 1e-3 precision
   * @param time {number}
   * @returns {number}
   */
  round(time) {
    return Math.round(time * this.PRECISION + this.EPSILON) / this.PRECISION;
  },

  /**
   * auxiliary method to check floating number proximity
   * @param min {number}
   * @param value {number}
   * @param max {number}
   * @param [o] {object}
   * @param [o.includeMin] {boolean}
   * @param [o.includeMax] {boolean}
   * @returns {boolean}
   */
  between(min, value, max, o) {
    o = Object.assign(
      {
        includeMin: true,
        includeMax: true,
      },
      o
    );
    return (
      (o.includeMin ? min <= value : min < value) &&
      (o.includeMax ? value <= max : value < max)
    );
  },

  /**
   * auxiliary method to get the lowest 2 digits of the truncated time (-.-XX)
   * to be used for matching time with frame edges
   * @param time {number}
   * @returns {number}
   * @private
   */
  _last2digits(time) {
    time = Math.trunc(time * this.PRECISION + this.EPSILON); // shift sec to ms
    if (time < 100) {
      return time;
    } else {
      return parseInt(time.toString().slice(-2), 10); // last 2 digits
    }
  },

  /**
   * auxiliary method to check if given time is at frame border
   * @param time {number}
   * @returns {boolean}
   */
  isEdge(time) {
    const td = this._last2digits(time);
    return this.FRAME_EDGES.indexOf(td) >= 0;
  },

  /**
   * gets start time of the frame (frame count starts from 1)
   * @param frame {number}
   * @returns {number}
   */
  time(frame) {
    console.assert(frame >= 1, 'expected nonzero frame index');
    return this.trunc((frame - 1) * this.SPF + this.EPSILON);
  },

  /**
   * get normalized video duration and frame count (30FPS)
   * @param time {number}
   * @returns {{duration: number, frames: number}}
   */
  videoDuration(time) {
    const edgeAtLeft = this.frameStart(time);
    const frameIndex = this.index(edgeAtLeft) - 1;
    return {
      duration: edgeAtLeft,
      frames: frameIndex,
    };
  },

  /**
   * gets nearest frame border at left, if already at frame edge - just truncates given time
   * @param time {number}
   * @returns {number}
   */
  frameStart(time) {
    let rv;
    if (this.isEdge(time)) {
      rv = time;
    } else {
      const delta = time % this.SPF;
      rv = time - delta;
    }
    return this.trunc(rv);
  },

  /**
   * gets nearest frame border at right, if already at frame edge - just truncates given time
   * @param time {number}
   * @returns {number}
   */
  frameEnd(time) {
    let rv;
    if (this.isEdge(time)) {
      rv = time;
    } else {
      const delta = time % this.SPF;
      rv = time - delta + this.SPF;
    }
    return this.trunc(rv);
  },

  /**
   * gets frame number at given time, if given left frame edge (frame start time) - reports index of that frame
   * @param time {number}
   * @returns {number}
   */
  index(time) {
    time = this.frameStart(time);
    const rv = 1 + time / this.SPF;
    return Math.round(rv);
  },
};
/* DBG */
globalThis && (globalThis.fg = FrameGrid);
export default FrameGrid;
